/**
 * Authentication screens.
 *
 * §5 requires: Sign In, MFA Prompt, Forgot Password, Password Reset, and the
 * four-step first sign-in — Password, TOTP, Security Key, Recovery Codes.
 *
 * §42: Workspace SSO-only users do not need a local password. Their enrolment
 * enters this flow at the appropriate MFA step. A key can be added later;
 * protected administration remains unavailable until WebAuthn is enrolled.
 * §96: the policy summary shown here reads from the server, so it can never
 * describe a control the deployment does not have.
 */

import { useEffect, useState } from 'react';
import { AagMark } from '../components/AagMark.js';
import { AccountAvatar } from '../components/AppShell.js';
import * as QRCode from 'qrcode';

function AuthFrame({ title, subtitle, children }: {
  title: string; subtitle?: string; children: React.ReactNode;
}) {
  return (
    <div className="min-h-screen flex items-start desktop:items-center justify-center px-4 py-8">
      <div className="w-full max-w-[460px]">
        <div className="mb-8 flex items-center gap-3">
          <AagMark className="h-5 w-auto" />
          <span className="h-5 w-px bg-neutral-400" aria-hidden />
          <span className="font-heading text-h4 font-semibold">Veritas</span>
          <span className="ml-auto"><AccountAvatar initials="?" presence="logged-out" /></span>
        </div>
        <h1 className="mb-1">{title}</h1>
        {subtitle && <p className="text-base text-neutral-700 mb-6">{subtitle}</p>}
        {children}
      </div>
    </div>
  );
}

export function MobileActivationScreen({
  busy, error, onVerify,
}: {
  busy: boolean;
  error: string | null;
  onVerify: (username: string, password: string, code: string) => void;
}) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');

  return (
    <AuthFrame
      title="Activate this device"
      subtitle="Sign in once. VERITAS creates and protects this phone's device identity automatically."
    >
      <div className="mb-4 p-3 border border-neutral-300 rounded-lg">
        <p className="font-semibold">Protected device identity</p>
        <p className="text-sm text-neutral-700">
          Created inside Android's secure key system. No certificate file or import password is needed.
        </p>
      </div>

      {error && (
        <div role="alert" className="mb-4 p-3 bg-alert-100 border border-alert-300 rounded-lg">
          <p className="text-base text-alert-800">{error}</p>
        </div>
      )}

      <label className="field">
        <span>Work username</span>
        <input
          className="input" value={username} autoCapitalize="none" autoCorrect="off"
          autoComplete="username" onChange={(event) => setUsername(event.target.value)}
        />
      </label>
      <label className="field">
        <span>Password</span>
        <input
          className="input" value={password} type="password" autoComplete="current-password"
          onChange={(event) => setPassword(event.target.value)}
        />
      </label>
      <label className="field">
        <span>Authenticator code</span>
        <input
          className="input tracking-widest" value={code} inputMode="numeric"
          autoComplete="one-time-code" maxLength={6} pattern="[0-9]{6}"
          onChange={(event) => setCode(event.target.value.replace(/\D/gu, '').slice(0, 6))}
        />
      </label>
      <button
        className="btn btn-primary btn-field"
        disabled={busy || !username.trim() || !password || code.length !== 6}
        onClick={() => onVerify(username.trim(), password, code)}
      >
        {busy ? 'Activating securely…' : 'Activate and open VERITAS'}
      </button>

      <p className="mt-4 text-sm text-neutral-700">
        Activation happens once. Access stops if this device is revoked or the account is disabled.
      </p>
    </AuthFrame>
  );
}

// ---------------------------------------------------------------------------

export function SignInScreen({
  ssoEnabled, localEnabled, error, onSso, onLocalSignIn, onForgot,
}: {
  ssoEnabled: boolean;
  localEnabled: boolean;
  error: string | null;
  onSso: () => void;
  onLocalSignIn: (username: string, password: string) => void;
  onForgot: () => void;
}) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  return (
    <AuthFrame title="Sign in" subtitle="Use your organization account.">
      {error && (
        <div role="alert" className="mb-4 p-3 bg-alert-100 border border-alert-300 rounded-lg">
          {/* The server returns an identical message for unknown and known
              accounts, so this never reveals whether an account exists. §46 */}
          <p className="text-base text-alert-800 whitespace-pre-line">{error}</p>
        </div>
      )}

      {ssoEnabled && (
        <button className="btn btn-primary btn-field mb-4" onClick={onSso}>
          Sign in with Google Workspace
        </button>
      )}

      {/* §5: the local form is preserved but only rendered when policy enables
          it. Workspace SSO-only users never see a password field. §42 */}
      {localEnabled && (
        <>
          {ssoEnabled && (
            <p className="text-center text-base text-neutral-700 my-4">or</p>
          )}

          <label className="field">
            <span>Username</span>
            <input
              className="input" value={username} autoComplete="username"
              onChange={(e) => setUsername(e.target.value)}
            />
          </label>

          <label className="field">
            <span>Password</span>
            {/* §39: password managers and paste are allowed. Nothing here
                blocks onPaste or sets autocomplete="off". */}
            <input
              type="password" className="input" value={password}
              autoComplete="current-password"
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>

          <button
            className="btn btn-primary btn-field mt-2"
            onClick={() => onLocalSignIn(username, password)}
            disabled={!username || !password}
          >
            Sign in
          </button>

          <button className="btn btn-ghost btn-block mt-3" onClick={onForgot}>
            Forgot password
          </button>
        </>
      )}
    </AuthFrame>
  );
}

// ---------------------------------------------------------------------------

export type MfaMethod = 'webauthn' | 'totp' | 'recovery_code';

export function MfaPromptScreen({
  available, method, error, onMethodChange, onSubmit, onUseSecurityKey,
}: {
  /** §43: SMS is never in this list — it does not exist server-side. */
  available: MfaMethod[];
  method: MfaMethod;
  error: string | null;
  onMethodChange: (m: MfaMethod) => void;
  onSubmit: (code: string) => void;
  onUseSecurityKey: () => void;
}) {
  const [code, setCode] = useState('');

  return (
    <AuthFrame title="Verify it's you" subtitle="Your account requires a second factor.">
      {error && (
        <div role="alert" className="mb-4 p-3 bg-alert-100 border border-alert-300 rounded-lg">
          <p className="text-base text-alert-800">{error}</p>
        </div>
      )}

      {method === 'webauthn' && (
        <button className="btn btn-primary btn-field mb-4" onClick={onUseSecurityKey}>
          Use YubiKey or security key
        </button>
      )}

      {(method === 'totp' || method === 'recovery_code') && (
        <>
          <label className="field">
            <span>{method === 'totp' ? 'Authenticator code' : 'Recovery code'}</span>
            <input
              className="input tracking-widest"
              value={code}
              inputMode={method === 'totp' ? 'numeric' : 'text'}
              autoComplete="one-time-code"
              maxLength={method === 'totp' ? 6 : 24}
              onChange={(e) => setCode(e.target.value)}
            />
          </label>
          <button
            className="btn btn-primary btn-field"
            onClick={() => onSubmit(code)}
            disabled={code.length < 6}
          >
            Verify
          </button>
        </>
      )}

      <div className="mt-6 flex flex-col gap-1">
        {available.filter((m) => m !== method).map((m) => (
          <button key={m} className="btn btn-ghost" onClick={() => onMethodChange(m)}>
            {m === 'webauthn' && 'Use a YubiKey or security key instead'}
            {m === 'totp' && 'Use an authenticator code instead'}
            {m === 'recovery_code' && 'Use a recovery code instead'}
          </button>
        ))}
      </div>
    </AuthFrame>
  );
}

// ---------------------------------------------------------------------------

export function ForgotPasswordScreen({
  sent, onSubmit, onBack,
}: { sent: boolean; onSubmit: (email: string) => void; onBack: () => void }) {
  const [email, setEmail] = useState('');

  return (
    <AuthFrame title="Reset your password">
      {sent ? (
        // §46: identical response for known and unknown addresses. The copy is
        // deliberately non-committal about whether an account exists.
        <>
          <p className="text-base mb-6">
            If that address belongs to a Veritas account, a reset link is on its
            way. The link is valid for 30 minutes and can be used once.
          </p>
          <button className="btn btn-secondary btn-block" onClick={onBack}>
            Back to sign in
          </button>
        </>
      ) : (
        <>
          <label className="field">
            <span>Email address</span>
            <input
              type="email" className="input" value={email} autoComplete="email"
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <button
            className="btn btn-primary btn-field"
            onClick={() => onSubmit(email)}
            disabled={!email}
          >
            Send reset link
          </button>
          <button className="btn btn-ghost btn-block mt-3" onClick={onBack}>Cancel</button>
        </>
      )}
    </AuthFrame>
  );
}

// ---------------------------------------------------------------------------

export interface PasswordPolicyView {
  minLength: number;
  maxLength: number;
  /** §96: rendered from server state, never hard-coded here. */
  summary: string[];
}

export function PasswordResetScreen({
  policy, error, onSubmit,
}: {
  policy: PasswordPolicyView;
  error: string | null;
  onSubmit: (password: string) => void;
}) {
  return (
    <AuthFrame title="Choose a new password">
      <PasswordFields policy={policy} error={error} submitLabel="Set password" onSubmit={onSubmit} />
    </AuthFrame>
  );
}

/**
 * Shared password entry. §39: minimum 15 characters, at least 128 supported,
 * spaces allowed, paste allowed, no composition rules. The only client-side
 * check is length — every other rule (breach list, common list, username and
 * product-name variants) is server-side, because a client-side blocklist would
 * have to ship the list.
 */
function PasswordFields({
  policy, error, submitLabel, onSubmit,
}: {
  policy: PasswordPolicyView;
  error: string | null;
  submitLabel: string;
  onSubmit: (password: string) => void;
}) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');

  const tooShort = password.length > 0 && password.length < policy.minLength;
  const mismatch = confirm.length > 0 && password !== confirm;
  const ready = password.length >= policy.minLength && password === confirm;

  return (
    <>
      {error && (
        <div role="alert" className="mb-4 p-3 bg-alert-100 border border-alert-300 rounded-lg">
          <p className="text-base text-alert-800">{error}</p>
        </div>
      )}

      <label className="field">
        <span>New password</span>
        <input
          type="password" className="input" value={password}
          autoComplete="new-password" maxLength={policy.maxLength}
          onChange={(e) => setPassword(e.target.value)}
        />
      </label>
      {tooShort && (
        <p className="text-base text-alert-600 -mt-2 mb-3">
          At least {policy.minLength} characters.
        </p>
      )}

      <label className="field">
        <span>Confirm password</span>
        <input
          type="password" className="input" value={confirm}
          autoComplete="new-password" maxLength={policy.maxLength}
          onChange={(e) => setConfirm(e.target.value)}
        />
      </label>
      {mismatch && (
        <p className="text-base text-alert-600 -mt-2 mb-3">Passwords do not match.</p>
      )}

      <ul className="text-base text-neutral-700 mb-4 list-disc pl-5">
        {policy.summary.map((s) => <li key={s}>{s}</li>)}
      </ul>

      <button
        className="btn btn-primary btn-field"
        onClick={() => onSubmit(password)}
        disabled={!ready}
      >
        {submitLabel}
      </button>
    </>
  );
}

// ---------------------------------------------------------------------------
// First sign-in, four steps. §42
// ---------------------------------------------------------------------------

export type FirstSignInStep = 'password' | 'totp' | 'security_key' | 'recovery_codes';

export function FirstSignInProgress({ step, steps }: {
  step: FirstSignInStep; steps: FirstSignInStep[];
}) {
  const i = steps.indexOf(step);
  return (
    <p className="text-base text-neutral-700 mb-6">
      Step {i + 1} of {steps.length}{step === 'security_key' ? ' (optional)' : ''}
    </p>
  );
}

export function FirstSignInPasswordScreen({
  policy, error, steps, onSubmit,
}: {
  policy: PasswordPolicyView; error: string | null;
  steps: FirstSignInStep[]; onSubmit: (password: string) => void;
}) {
  return (
    <AuthFrame title="Set your password">
      <FirstSignInProgress step="password" steps={steps} />
      <PasswordFields policy={policy} error={error} submitLabel="Continue" onSubmit={onSubmit} />
    </AuthFrame>
  );
}

export function FirstSignInTotpScreen({
  secret, otpauthUri, error, steps, onVerify,
}: {
  /** Shown once for manual entry. Never persisted client-side. §82 */
  secret: string;
  otpauthUri: string;
  error: string | null;
  steps: FirstSignInStep[];
  onVerify: (code: string) => void;
}) {
  const [code, setCode] = useState('');
  const [qrDataUri, setQrDataUri] = useState<string>('');

  useEffect(() => {
    let cancelled = false;
    if (!otpauthUri) {
      setQrDataUri('');
      return;
    }
    void QRCode.toDataURL(otpauthUri, {
      errorCorrectionLevel: 'M',
      margin: 1,
      width: 240,
    }).then((uri: string) => {
      if (!cancelled) setQrDataUri(uri);
    });
    return () => { cancelled = true; };
  }, [otpauthUri]);

  return (
    <AuthFrame title="Add an authenticator" subtitle="Scan the QR code with your authenticator app.">
      <FirstSignInProgress step="totp" steps={steps} />

      <div className="card mb-4">
        <p className="card-kicker">Authenticator QR code</p>
        {qrDataUri ? (
          <div className="flex justify-center my-4">
            <img
              src={qrDataUri}
              width={240}
              height={240}
              alt="Authenticator setup QR code"
              className="bg-white p-2"
            />
          </div>
        ) : (
          <p className="text-base text-neutral-700 my-4">Preparing QR code…</p>
        )}
        <p className="text-sm text-neutral-700 mb-1">
          Cannot scan it? Enter this setup key manually:
        </p>
        <p className="font-mono text-base break-all select-all">{secret}</p>
      </div>

      {error && (
        <div role="alert" className="mb-4 p-3 bg-alert-100 border border-alert-300 rounded-lg">
          <p className="text-base text-alert-800">{error}</p>
        </div>
      )}

      <label className="field">
        <span>Enter the 6-digit code</span>
        <input
          className="input tracking-widest" value={code} inputMode="numeric" maxLength={6}
          autoComplete="one-time-code"
          onChange={(e) => setCode(e.target.value)}
        />
      </label>

      <button
        className="btn btn-primary btn-field"
        onClick={() => onVerify(code)}
        disabled={code.length !== 6}
      >
        Verify and continue
      </button>
    </AuthFrame>
  );
}

export function FirstSignInSecurityKeyScreen({
  error, steps, onEnroll, onSkip,
}: {
  error: string | null;
  steps: FirstSignInStep[];
  onEnroll: (name: string) => void;
  onSkip: () => void;
}) {
  const [name, setName] = useState('');

  return (
    <AuthFrame
      title="Add a YubiKey or passkey"
      subtitle="Use one now, or finish setup with your authenticator and add it later."
    >
      <FirstSignInProgress step="security_key" steps={steps} />

      {error && (
        <div role="alert" className="mb-4 p-3 bg-alert-100 border border-alert-300 rounded-lg">
          <p className="text-base text-alert-800">{error}</p>
        </div>
      )}

      {/* §44: authenticators are named, so a lost one can be revoked precisely. */}
      <label className="field">
        <span>Name this key</span>
        <input
          className="input" value={name} placeholder="Office YubiKey"
          onChange={(e) => setName(e.target.value)}
        />
      </label>

      <button
        className="btn btn-primary btn-field"
        onClick={() => onEnroll(name || 'Security key')}
      >
        Add YubiKey or security key
      </button>

      <button className="btn btn-secondary btn-block mt-3" onClick={onSkip}>
        Set up later
      </button>
      <p className="text-sm text-neutral-700 mt-3">
        You can add a YubiKey or passkey from Profile, then Security. Protected
        administration stays locked until one is registered.
      </p>
    </AuthFrame>
  );
}

export function FirstSignInRecoveryCodesScreen({
  codes, steps, onDone,
}: {
  /** §45: shown in plaintext exactly once. The server stores only a verifier. */
  codes: string[];
  steps: FirstSignInStep[];
  onDone: () => void;
}) {
  const [confirmed, setConfirmed] = useState(false);

  return (
    <AuthFrame title="Save your recovery codes">
      <FirstSignInProgress step="recovery_codes" steps={steps} />

      <p className="text-base mb-4">
        These codes are shown once and cannot be retrieved later. Each one works
        a single time.
      </p>

      <ul className="card font-mono text-base grid grid-cols-2 gap-2 mb-4 select-all">
        {codes.map((c) => <li key={c}>{c}</li>)}
      </ul>

      <label className="flex items-center gap-3 py-3 min-h-touch cursor-pointer">
        <input
          type="checkbox" checked={confirmed} className="w-5 h-5 accent-accent"
          onChange={(e) => setConfirmed(e.target.checked)}
        />
        <span className="text-base">I have saved these codes somewhere safe</span>
      </label>

      <button className="btn btn-primary btn-field" onClick={onDone} disabled={!confirmed}>
        Finish
      </button>
    </AuthFrame>
  );
}
